import { getAllFactoringReSellingNominees } from 'lib/api/reSell/factoring';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import {
  calculateNomineeSummary,
  validatePermissions,
} from '../../../../../lib/functions/utils';
import MultiResellMain from '../FactoringFundReSell/components/NewMultiResell';

const FactoringResellsNominees: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [resellNominees, setResellNominees] = useState<any>([]);
  const [resells, setResells] = useState<any>([]);
  const [amountToApply, setAmountToApply] = useState(0);
  const [selectedNominee, setSelectedNominee] = useState<any>(null);
  const [openMultiSales, setOpenMultiSales] = useState(false);
  const [openNomineeResells, setOpenNomineeResells] = useState(false);
  const [isNomineeReadOnly, setIsNomineeReadOnly] = useState(true);
  const [nomineeId, setNomineeId] = useState('');
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [folio, setFolio] = useState('');
  const [fundId, setFundId] = useState<string | null>(null);
  const [selectedFund, setSelectedFund] = useState<any>(null);
  const [startCreatedAt, setStartCreatedAt] = useState<string | null>(null);
  const [endCreatedAt, setEndCreatedAt] = useState<string | null>(null);
  const [startOperationDate, setStartOperationDate] = useState<string | null>(
    null,
  );
  const [endOperationDate, setEndOperationDate] = useState<string | null>(null);

  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [selectedSortBy, setSelectedSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const generateQuery = ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    let query = '';
    if (nomineeId) query += `nomineeId=${nomineeId}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (stakeholderIdentifier)
      query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
    if (folio) query += `folio=${folio}&`;
    if (fundId) query += `fundId=${fundId}&`;
    if (startCreatedAt) query += `startCreatedAt=${startCreatedAt}&`;
    if (endCreatedAt) query += `endCreatedAt=${endCreatedAt}&`;
    if (startOperationDate)
      query += `startOperationDate=${startOperationDate}&`;
    if (endOperationDate) query += `endOperationDate=${endOperationDate}&`;
    if (selectedSortBy) query += `sortBy=${selectedSortBy}&`;
    if (sortOrder) query += `sortOrder=${sortOrder}&`;
    if (page !== undefined && page !== null)
      query += `page=${page}&pageSize=${pageSize}&`;
    if (exportComplete) query += `all=true&`;
    return query;
  };

  const fetchResellings = async ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    setLoading(true);
    try {
      const apiPage = page === 0 ? 1 : page;
      const query = generateQuery({ page: apiPage, pageSize, exportComplete });
      const response = await getAllFactoringReSellingNominees(dispatch, query);

      const processedNominees = response.data.map((nominee: any) => {
        const resells = nominee.resells || [];
        const fundName = resells.length > 0 ? resells[0].fundName : '';
        const fundId = resells.length > 0 ? resells[0].fundId : '';

        const summary = calculateNomineeSummary(nominee);

        return {
          ...nominee,
          ...summary,
          amountToReceive:
            nominee.amountToReceive !== undefined &&
            nominee.amountToReceive !== null &&
            nominee.amountToReceive > 0
              ? nominee.amountToReceive
              : summary.amountToReceive,
          resellsCount: resells.length,
          fundName,
          fundId,
        };
      });
      if (!exportComplete) {
        setResellNominees(processedNominees);
        setPagination(response.pagination);
      }
      setLoading(false);
      return {
        data: processedNominees,
        pagination: response.pagination,
      };
    } catch (error) {
      alert('Error al cargar los nóminas');
      setLoading(false);
    }
  };

  const triggerSearch = async () => {
    await fetchResellings({ page: 1, pageSize: PAGE_SIZE });
  };

  const handleNomineeDetails = async (nomineeId: number) => {
    const nominee = resellNominees.find((n: any) => n.id === nomineeId);

    if (!nominee) {
      console.error('Nominee not found:', nomineeId);
      return;
    }

    setSelectedNominee(nominee);
    setResells(nominee.resells || []);
    setAmountToApply(nominee.amountToApply || 0);
    setIsNomineeReadOnly(true);
    setOpenNomineeResells(true);
  };

  const handleNomineeResellsModalClose = (open: boolean) => {
    setOpenNomineeResells(open);
    if (!open) {
      setSelectedNominee(null);
      setResells([]);
      setIsNomineeReadOnly(true);
    }
  };

  const filters: Filter[] = [
    {
      type: 'text',
      label: 'ID',
      value: nomineeId,
      setter: setNomineeId,
    },
    {
      type: 'text',
      label: 'Folio',
      value: folio,
      setter: setFolio,
    },
    {
      type: 'text',
      label: 'Rut cliente',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      type: 'text',
      label: 'Rut deudor',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      type: 'autocomplete',
      label: 'Fondo',
      value: fundId,
      key: 'fundName',
      setter: (value: string | null) => {
        setFundId(value);
      },
      selectedValue: selectedFund,
      setSelectedValue: (value: any) => {
        setSelectedFund(value);
        if (value?.object?.id) {
          setFundId(value.object.id);
        } else {
          setFundId(null);
        }
      },
    },
    {
      type: 'date',
      label: 'Fecha de creación',
      value: startCreatedAt,
      setter: setStartCreatedAt,
      dateKey: 'startCreatedAt',
      dateType: 'created',
      pairKey: 'endCreatedAt',
    },
    {
      type: 'date',
      label: 'Fecha de creación',
      value: endCreatedAt,
      setter: setEndCreatedAt,
      dateKey: 'endCreatedAt',
      dateType: 'created',
      pairKey: 'startCreatedAt',
    },
    {
      type: 'date',
      label: 'Fecha de operación',
      value: startOperationDate,
      setter: setStartOperationDate,
      dateKey: 'startOperationDate',
      dateType: 'request',
      pairKey: 'endOperationDate',
    },
    {
      type: 'date',
      label: 'Fecha de operación',
      value: endOperationDate,
      setter: setEndOperationDate,
      dateKey: 'endOperationDate',
      dateType: 'request',
      pairKey: 'startOperationDate',
    },
  ];

  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'primary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={() => triggerSearch()}
          selectedSortBy={selectedSortBy}
          setSelectedSortBy={setSelectedSortBy}
          sortOptions={[
            { value: 'createdAt', label: 'Fecha de creación' },
            { value: 'operationDate', label: 'Fecha de operación' },
          ]}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          dispatch={dispatch}
        />
      ),
    },
    {
      action: () => setOpenMultiSales(true),
      name: 'Registrar Nómina',
      color: 'primary',
      disabled:
        !validatePermissions(user, 'financing_fundSale_new') &&
        !validatePermissions(user, 'financing_fundSale_new_payment'),
    },
  ];

  useEffect(() => {
    void fetchResellings({ page: 1, pageSize: PAGE_SIZE });
  }, []);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        title="Acá encontrarás todas las ventas a fondos agrupadas por nóminas."
        variant="body1"
        buttons={buttonsData}
      />

      <TableWithPagination
        dataHeaders={[
          { key: 'id', label: 'ID' },
          { key: 'operationDate', label: 'Fecha', type: 'date' },
          { key: 'fundName', label: 'Fondo' },
          { key: 'fundId', label: 'Rut Fondo' },
          { key: 'resellsCount', label: 'Docs' },
          { key: 'totalAmount', label: 'Total', type: 'money' },
          { key: 'avgRate', label: 'Tasa Promedio', type: 'percentage-soft' },
          {
            key: 'operationCostFixedWithIVA',
            label: 'Comisión + IVA',
            type: 'money',
          },
          { key: 'rateCost', label: 'Diferencia de precio', type: 'money' },
          { key: 'amountToApply', label: 'Aplicado', type: 'money' },
          { key: 'amountToReceive', label: 'Recibido', type: 'money' },
        ]}
        data={resellNominees}
        setData={setResellNominees}
        handleClickRow={handleNomineeDetails}
        exportToFile="xlsx"
        exportComplete
        rowsPerPageDefault={10}
        searchData={fetchResellings}
        pagination={pagination}
        setPagination={setPagination}
        loading={loading}
        setLoading={setLoading}
      />

      {openMultiSales && (
        <MultiResellMain
          open={openMultiSales}
          setOpen={setOpenMultiSales}
          setUp={fetchResellings}
          pagination={pagination}
          resellNominees={resellNominees}
        />
      )}
      {openNomineeResells && (
        <MultiResellMain
          open={openNomineeResells}
          setOpen={handleNomineeResellsModalClose}
          setUp={fetchResellings}
          pagination={pagination}
          initialResells={resells}
          initialAmountToApply={amountToApply}
          initialFundId={selectedNominee?.fundId}
          initialOperationDate={new Date(selectedNominee?.operationDate)}
          initialApplications={selectedNominee?.applications}
          initialApplicationsIds={selectedNominee?.applications.map(
            (application: any) => application.id,
          )}
          nomineeId={selectedNominee?.id}
          readOnly={isNomineeReadOnly}
          setReadOnly={setIsNomineeReadOnly}
          nominee={selectedNominee}
        />
      )}
    </div>
  );
};

export default FactoringResellsNominees;
